import { type CSSProperties } from "react";
import type { CustomQuestionsV2 } from "@lib/config/types";
import { extractBlocks } from "@lib/config/types";
import {
  textBlockWidth,
  type FontFamily,
  resolveFontFamilyVariables,
  alignStyle,
} from "@lib/utils";
import { Question } from "@/components/blocks/questions/question";
import type { Ticket } from "@lib/ticket";
import { filterAlreadyAnsweredQuestions } from "@lib/custom-questions";
import type { FlowType } from "./blocks";

type CustomQuestionsProps = CustomQuestionsV2 & {
  ticket?: Ticket;
  flowType?: FlowType;
};

type CustomQuestionStyles = CSSProperties & {
  "--label-font-name": string;
  "--label-font-weight": string;
  "--label-font-style": string;
  "--label-color": string;
  "--label-font-size": string;
  "--label-line-height": string;
  "--label-letter-spacing": string;
  "--prompt-font-name": string;
  "--prompt-font-weight": string;
  "--prompt-font-style": string;
  "--prompt-font-color": string;
  "--prompt-font-size": string;
  "--input-font-name": string;
  "--input-font-weight": string;
  "--input-font-style": string;
  "--input-color": string;
  "--input-background": string;
  "--input-border-color": string;
  "--placeholder-color": string;
  "--field-border-size": string;
  "--field-border-top-left-radius": string;
  "--field-border-top-right-radius": string;
  "--field-border-bottom-right-radius": string;
  "--field-border-bottom-left-radius": string;
};

export function CustomQuestions({
  custom_questions,
  ticket,
  flowType,
}: CustomQuestionsProps) {
  const questions = filterAlreadyAnsweredQuestions(
    extractBlocks(custom_questions.questions),
    ticket?.custom_questions,
  );

  const labelFontFamily = custom_questions.label_style.font as FontFamily;
  const promptFontFamily = custom_questions.prompt.font as FontFamily;
  const inputFontFamily = custom_questions.input_style.font as FontFamily;

  const isPill =
    custom_questions.input_style.border.radius === 9999.0 ||
    custom_questions.input_style.border.radius === 99999.0;
  return (
    <div
      data-block-type="Custom Questions"
      className="pt-[1.125em] grid gap-8"
      style={
        {
          maxWidth: textBlockWidth(custom_questions.width),
          ...alignStyle(custom_questions),
          ...resolveFontFamilyVariables(
            "--label",
            labelFontFamily,
            custom_questions.label_style.font_properties,
          ),
          "--label-color": custom_questions.label_style.color,
          "--label-font-size": `${(custom_questions.label_style as unknown as Record<string, number>).font_size ?? 16}px`,
          "--label-line-height": `${(custom_questions.label_style as unknown as Record<string, number>).line_height ?? 24}px`,
          "--label-letter-spacing": `${(custom_questions.label_style as unknown as Record<string, number>).letter_spacing ?? 0}px`,
          ...resolveFontFamilyVariables(
            "--prompt",
            promptFontFamily,
            custom_questions.prompt.font_properties,
          ),
          "--prompt-font-color": custom_questions.prompt.color,
          "--prompt-font-size": `${custom_questions.prompt.font_size}px`,
          ...resolveFontFamilyVariables(
            "--input",
            inputFontFamily,
            custom_questions.input_style.font_properties,
          ),
          "--input-color": custom_questions.input_style.color,
          "--input-background": custom_questions.input_style.background_color,
          "--input-border-color": custom_questions.input_style.border.color,
          "--placeholder-color": custom_questions.input_style.placeholder_color,
          "--field-border-size": `${custom_questions.input_style.border.width}px`,
          "--field-border-top-left-radius": `${(isPill ? 9999 : custom_questions.input_style.border.radius) / 2}px`,
          "--field-border-top-right-radius": `${(isPill ? 9999 : custom_questions.input_style.border.radius) / 2}px`,
          "--field-border-bottom-right-radius": `${(isPill ? 9999 : custom_questions.input_style.border.radius) / 2}px`,
          "--field-border-bottom-left-radius": `${(isPill ? 9999 : custom_questions.input_style.border.radius) / 2}px`,
        } as CustomQuestionStyles
      }
    >
      {questions.map((question, index) => (
        <Question
          key={`custom-question-${question.type}-${index}`}
          flowType={flowType}
          {...question}
        />
      ))}
    </div>
  );
}
